import * as React from "react";
import { EditButton } from "../../../../components/Buttons/EditButton";
import { AdvancedEmailWizardContext } from "../../../../context/AdvancedEmailWizardContext/AdvancedEmailWizardContext";
import { WizardContext } from "../../../../components/Wizard/Context";
import { AttachmentContext } from "../../../../context/AttachmentContext/AttachmentContext";
import { strings } from "../../../../localisation";
import { AttachmentListItems, FileProcessingStatus } from "../../Attachments";
import { EmailList } from "../EmailList/EmailList";
import { SummaryDetailsDiv, SummaryFieldDiv, SummaryFieldFooterSpan, SummaryFieldSpan, SummaryPanelDiv, SummaryTitleDiv, SummaryTitleH2 } from "./SummaryPanel.styles";
import { WizardTypeEnum } from "../../../../enums/WizardTypeEnum";

export interface ISummaryProps {
    SummaryTitle: string;
    TemplateName?: string;
    EmailName: string;
    ToAddressTypeStrings?: string[];
    CCAddressTypeStrings?: string[];
    BCCAddressTypeStrings?: string[];
    EmailCC: boolean;
    EmailBCC: boolean;
    CCFirstOnly: boolean;
    BCCFirstOnly: boolean;
    RecipientCount: number;
    ViewOnPortal: boolean;
    CopyToCorrespondence: boolean;
    Subject: string;
    FromDescription: string;
    FromAddress: string;
}

export const SummaryPanel = (props: ISummaryProps) => {
    // Context
    const { WizardType } = React.useContext(AdvancedEmailWizardContext);
    const attachmentContext = React.useContext(AttachmentContext);
    const { Steps, SetStep } = React.useContext(WizardContext);

    const visibleAttachments = attachmentContext.Attachments
        .filter((attachment) => attachment.ProcessingStatus !== FileProcessingStatus.Deleted);

    const detailsStepIndex = Steps && Steps.find((step) => {
        return [strings.Label_Summary_EmailDetails, strings.Label_Step_TemplateDetails_Title].indexOf(step.TrackerProps.Title) >= 0;
    });

    const emailFrom = `${props.FromDescription} ${props.FromAddress.length > 0 ? `- ${props.FromAddress}` : ``}`;

    let recipientsAddresses = props.ToAddressTypeStrings?.join(", ");
    const addressTypeCount = props.ToAddressTypeStrings!.length;
    const recipientCount = props.RecipientCount;
    const isTemplateWizard = WizardType === WizardTypeEnum.Template;

    const recipientCountLabel = recipientCount > 1 ? strings.Label_Summary_RecipientsTo : strings.Label_Summary_RecipientTo;
    const addressCountLabel = addressTypeCount > 1 ? strings.Label_Summary_Addresses : strings.Label_Summary_Address;

    if (isTemplateWizard || recipientCount === 1) {
        recipientsAddresses = `${addressCountLabel} ${recipientsAddresses}`;
    } else {
        recipientsAddresses = `${recipientCount} ${recipientCountLabel} ${addressCountLabel} ${recipientsAddresses}`;
    }

    return (
        <SummaryPanelDiv>
            <SummaryTitleDiv>
                <SummaryTitleH2>
                    {props.SummaryTitle}
                </SummaryTitleH2>
                <EditButton
                    Caption="Edit"
                    OnClick={() => detailsStepIndex && SetStep!(Steps!.indexOf(detailsStepIndex))}
                />
            </SummaryTitleDiv>

            <SummaryDetailsDiv>

                {props.TemplateName && (
                    <SummaryFieldDiv>
                        <SummaryFieldSpan>{strings.Label_Template_Name}</SummaryFieldSpan>
                        <div>{props.TemplateName}</div>
                    </SummaryFieldDiv>
                )}

                <SummaryFieldDiv>
                    <SummaryFieldSpan>{strings.Label_Email_Name}</SummaryFieldSpan>
                    <div>{props.EmailName}</div>
                </SummaryFieldDiv>

                <SummaryFieldDiv>
                    <SummaryFieldSpan>{strings.Label_Email_To}</SummaryFieldSpan>
                    <div>
                        {recipientsAddresses}
                    </div>
                </SummaryFieldDiv>

                {props.EmailCC &&
                    <SummaryFieldDiv>
                        <SummaryFieldSpan>{strings.Label_Email_CC}</SummaryFieldSpan>
                        <EmailList
                            AddressTypes={props.CCAddressTypeStrings}
                            RecipientCount={props.RecipientCount}
                            FirstEmailOnly={props.CCFirstOnly}
                        />
                    </SummaryFieldDiv>
                }

                {props.EmailBCC &&
                    <SummaryFieldDiv>
                        <SummaryFieldSpan>{strings.Label_Email_BCC}</SummaryFieldSpan>
                        <EmailList
                            AddressTypes={props.BCCAddressTypeStrings}
                            RecipientCount={props.RecipientCount}
                            FirstEmailOnly={props.BCCFirstOnly}
                        />
                    </SummaryFieldDiv>
                }

                <SummaryFieldDiv>
                    <SummaryFieldSpan>{strings.Label_Email_Subject}</SummaryFieldSpan>
                    <div>{props.Subject}</div>
                </SummaryFieldDiv>

                <SummaryFieldDiv>
                    <SummaryFieldSpan>{strings.Label_Email_From}</SummaryFieldSpan>
                    <div>{emailFrom}</div>
                </SummaryFieldDiv>

                {
                    visibleAttachments.length > 0 &&
                    <SummaryFieldDiv>
                        <SummaryFieldSpan>
                            {`${strings.Label_Summary_Attachments} (${visibleAttachments.length})`}
                        </SummaryFieldSpan>
                        <AttachmentListItems
                            Attachments={attachmentContext.Attachments}
                        />
                        {props.CopyToCorrespondence && (
                            <SummaryFieldFooterSpan>
                                {strings.Message_Preview_CopyToCorrespondence}
                            </SummaryFieldFooterSpan>
                        )}
                    </SummaryFieldDiv>
                }

                <SummaryFieldDiv>
                    <SummaryFieldSpan>{strings.Label_Email_ShowInPortal}</SummaryFieldSpan>
                    <div>{props.ViewOnPortal ? strings.Label_Yes : strings.Label_No}</div>
                </SummaryFieldDiv>

            </SummaryDetailsDiv>
        </SummaryPanelDiv>
    );
};
